// @see https://sheetjs.com/
const XLSX = require('xlsx-js-style');

import store from '@/store';
import clock from '@/lib/clock';
import hash from '@/lib/helpers/hash';
// import toCurrency from '@/lib/helpers/toCurrency';
// import toPercentage from '@/lib/helpers/toPercentage';
import toDate from '@/lib/helpers/toDate';
import toDatetime from '@/lib/helpers/toDatetime';
import getTableRowValue from '@/lib/helpers/getTableRowValue';
import i18n from '@/lib/i18n';
import get from 'lodash/get';

export const getWorkbook = data => {
    return XLSX.read(data, { type: 'array' });
};

export const getSheetAsJson = (workbook, sheetName = 'Sheet1', options = {}) => {
    const sheet = workbook.Sheets[`${sheetName}`];

    // @see https://www.npmjs.com/package/xlsx#parsing-options
    options = { header: 1, raw: true, defval: null, ...options };

    return XLSX.utils.sheet_to_json(sheet, options);
};

export const createXlsx = () => {
    return XLSX.utils.book_new();
};

export const createAoaSheet = (workbook, data = [], options = null) => {
    return XLSX.utils.aoa_to_sheet(data, options);
};

export const createJsonSheet = (workbook, data = null, options = null) => {
    return XLSX.utils.json_to_sheet(data, options);
};

export const appendAoaToSheet = (worksheet, data = [], options = null) => {
    XLSX.utils.sheet_add_aoa(worksheet, data, options);
    return worksheet;
};

export const appendJsonToSheet = (worksheet, data = null, options = null) => {
    if (data) {
        XLSX.utils.sheet_add_json(worksheet, data, options);
    }
    return worksheet;
};

export const addSheet = (workbook, worksheet, name) => {
    if (name && name.length >= 31) {
        name = `${name.substring(0, 28)}...`;
    }

    XLSX.utils.book_append_sheet(workbook, worksheet, name);

    return workbook;
};

export const download = (workbook, name) => {
    XLSX.writeFile(workbook, `${name}.xlsx`);
};

export const toJson = (data, options = {}) => {
    const workbook = getWorkbook(data);

    let response = [];

    workbook.SheetNames.forEach(name => {
        response.push(getSheetAsJson(workbook, name, options));
    });

    return response;
};

export const toXlsx = (headers = [], json = [], name = null) => {
    name = name ? name : hash({ headers, json }, 8);
    name = `${clock().format('YYYY-MM-DD')}_${name}`;

    const worksheet = XLSX.utils.json_to_sheet(json, {
        header: headers,
        skipHeader: false
    });

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet);
    XLSX.writeFile(workbook, `${name}.xlsx`);
};

export const convertRowsToJson = (rows, headers) => {
    return rows.map(row => {
        return headers.reduce((acc, header) => {
            let value = getTableRowValue({ header, item: row, value: get(row, header.value) });

            if (header.format === 'number' && value) {
                if (header.value.endsWith('_moic')) {
                    value = {
                        v: value,
                        t: 'n',
                        z: `#,##0.00 X`
                    };
                } else {
                    value = {
                        v: value,
                        t: 'n',
                        z: `#,##0.00`
                    };
                }
            }

            if (header.format === 'money' && value && 'amount' in value && 'currency' in value) {
                const currency = store.getters['countries/countries'].find(
                    ({ currency }) => currency.code === value.currency
                )?.currency ||
                    store.getters['countries/countries'][0].currency || { symbol: '£' };
                value = {
                    v: value.amount,
                    t: 'n',
                    z: `${currency.symbol}#,##0.00`
                };
            }

            if (header.format === 'percentage' && value) {
                value = {
                    v: value,
                    t: 'n',
                    z: `#,##0.00%`
                };
            }

            if (header.format === 'date' && value) {
                value = toDate(value);
            }

            if (header.format === 'datetime' && value) {
                value = toDatetime(value);
            }

            if (header.format === 'boolean') {
                if (value !== null) {
                    value = value ? i18n.t('yes') : i18n.t('no');
                } else {
                    value = '';
                }
            }

            if (header.enum && header.enumA11yKey && value) {
                value = i18n.t(`${header.enumA11yKey}.${value}`);
            }

            acc[`${header.text}`] = value;
            return acc;
        }, {});
    });
};
